import { createContext } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useMemo, useState } from 'react';

import { useAiEmbeddedDesignerConfigurationQuery } from 'src/shared/graphql/generated';
import { useQueryParam } from 'src/shared/hooks/useQueryParam';
import type { CreateLeadResponse } from 'src/shared/requests/createLead';

export interface EmbedContext {
  isEmbed: boolean;
  embedOrgId?: number;
  embedConfigId?: number;
  originalImageId?: number;
  leadData: CreateLeadResponse | null;
  cta?: string;
  setEmbedOrgId: (value: number) => void;
  setEmbedConfigId: (value: number) => void;
  setLeadData: (value: CreateLeadResponse) => void;
  showLandingPage?: boolean;
  ctaLink?: string;
  leadFormHeader?: string;
  leadFormSubheader?: string;
  hasLeadForm: boolean;
  hasLeadFormFullName: boolean;
  hasLeadFormPhoneNumber: boolean;
  hasLeadFormPostalCode: boolean;
  sendHomeownerEmail: boolean;
  header: string;
  subheader: string;
}

const defaultValue: EmbedContext = {
  isEmbed: false,
  showLandingPage: undefined,
  originalImageId: undefined,
  leadData: null,
  cta: undefined,
  ctaLink: undefined,
  leadFormHeader: undefined,
  leadFormSubheader: undefined,
  hasLeadForm: true,
  hasLeadFormFullName: true,
  hasLeadFormPhoneNumber: true,
  hasLeadFormPostalCode: true,
  sendHomeownerEmail: true,
  header: '',
  subheader: '',
  setEmbedOrgId: () => {},
  setLeadData: () => {},
  setEmbedConfigId: () => {},
};

const [Provider, useContext] = createContext<EmbedContext | undefined>({
  strict: false,
});

export interface EmbedProviderProps extends PropsWithChildren<{}> {}

export const EmbedProvider = (props: EmbedProviderProps) => {
  const hasEmbedQueryParam = useQueryParam('isEmbed') === 'true';
  const [isEmbed, setIsEmbed] = useState(hasEmbedQueryParam);
  const [embedOrgId, setEmbedOrgId] = useState<number | undefined>(undefined);
  const [leadData, setLeadData] = useState<CreateLeadResponse | null>(null);
  const [embedConfigId, setEmbedConfigId] = useState<number | undefined>(
    undefined,
  );

  if (hasEmbedQueryParam && !isEmbed) setIsEmbed(true);

  const { data } = useAiEmbeddedDesignerConfigurationQuery({
    variables: { id: embedConfigId ? `${embedConfigId}` : '' },
    skip: !embedConfigId,
  });

  const value = useMemo(() => {
    const {
      ctaLink,
      sampleImageIds,
      cta,
      hasLeadForm,
      hasLandingPage,
      hasLeadFormFullName,
      hasLeadFormPhoneNumber,
      hasLeadFormPostalCode,
      leadFormHeader,
      leadFormSubheader,
      sendHomeownerEmail,
      header,
      subheader,
    } = data?.aiEmbeddedDesignerConfigurations || {};

    return {
      isEmbed,
      leadData,
      embedOrgId,
      embedConfigId,
      setEmbedOrgId,
      setEmbedConfigId,
      setLeadData,
      ctaLink: ctaLink ?? undefined,
      // MEOW-5614 - handle multiple images, not just one sample image
      originalImageId: sampleImageIds ? sampleImageIds[0] : undefined,
      cta,
      leadFormHeader,
      leadFormSubheader,
      header: header ?? '',
      subheader: subheader ?? '',
      hasLeadForm: hasLeadForm ?? true,
      hasLeadFormFullName: hasLeadFormFullName ?? true,
      hasLeadFormPhoneNumber: hasLeadFormPhoneNumber ?? true,
      hasLeadFormPostalCode: hasLeadFormPostalCode ?? true,
      showLandingPage: hasLandingPage,
      sendHomeownerEmail: sendHomeownerEmail ?? true,
    };
  }, [
    isEmbed,
    embedOrgId,
    leadData,
    setEmbedOrgId,
    setLeadData,
    embedConfigId,
    setEmbedConfigId,
    data,
  ]);

  return <Provider value={value} {...props} />;
};

export const useEmbedContext = () => useContext() ?? defaultValue;
